import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import Header from './components/Header15';
import Footer from './components/Footer15';

const FAQs = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            category: 'Orders & Shopping',
            questions: [
                {
                    question: 'How do I place an order?',
                    answer: 'Browse our collection, add items to your cart, and proceed to checkout. You\'ll need to create an account or sign in to complete your purchase. Follow the prompts to enter your shipping and payment information, then review and confirm your order.'
                },
                {
                    question: 'Can I modify or cancel my order?',
                    answer: 'Orders can be modified or canceled within 1 hour of placement. After this window, orders enter our processing system and cannot be changed. Please contact our customer service team immediately if you need to make changes to your order.'
                },
                {
                    question: 'Do you offer gift wrapping?',
                    answer: 'Yes! We offer complimentary gift wrapping on all orders. Simply select the gift wrapping option during checkout and add a personalized message. Your items will arrive beautifully packaged and ready to give.'
                },
                {
                    question: 'What payment methods do you accept?',
                    answer: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover), PayPal, Apple Pay, and Google Pay. All payments are processed securely through encrypted connections.'
                }
            ]
        },
        {
            category: 'Shipping & Delivery',
            questions: [
                {
                    question: 'How long does shipping take?',
                    answer: 'Standard shipping takes 5-7 business days, Express shipping takes 2-3 business days, and Next Day delivery is available for orders placed before 2 PM EST. Processing time is 1-2 business days for all orders.'
                },
                {
                    question: 'Do you ship internationally?',
                    answer: 'Yes, we ship to select countries worldwide. International shipping costs and delivery times vary by destination. Customs duties and taxes may apply and are the responsibility of the recipient.'
                },
                {
                    question: 'How can I track my order?',
                    answer: 'Once your order ships, you\'ll receive a shipping confirmation email with a tracking number. You can also log in to your account and view your order history to track your package in real-time.'
                },
                {
                    question: 'What if my package is lost or damaged?',
                    answer: 'If your package appears to be lost or arrives damaged, please contact us immediately at support@studio.com. We\'ll work with the carrier to locate your package or send a replacement at no additional cost to you.'
                }
            ]
        },
        {
            category: 'Returns & Exchanges',
            questions: [
                {
                    question: 'What is your return policy?',
                    answer: 'We accept returns within 30 days of delivery. Items must be unworn, unwashed, and in original condition with tags attached. We offer free return shipping with a prepaid label. Sale items are final sale and cannot be returned.'
                },
                {
                    question: 'How do I return an item?',
                    answer: 'Log in to your account, go to order history, select the order you want to return, and click "Request Return". Print the prepaid return label we email you, package your items securely, and drop off at any authorized shipping location.'
                },
                {
                    question: 'How long does it take to process a refund?',
                    answer: 'Once we receive your return, please allow 5-7 business days for processing. Refunds are issued to your original payment method and may take an additional 5-10 business days to appear in your account.'
                },
                {
                    question: 'Can I exchange an item?',
                    answer: 'We recommend placing a new order for the item you want and returning the original for a refund. This ensures you receive your new item quickly. For direct exchanges, please contact our customer service team.'
                }
            ]
        },
        {
            category: 'Products & Sizing',
            questions: [
                {
                    question: 'How do I find my size?',
                    answer: 'Each product page includes a detailed size guide with measurements. We recommend measuring yourself and comparing to our size charts for the best fit. If you\'re between sizes, we generally recommend sizing up for a more comfortable fit.'
                },
                {
                    question: 'Are your products true to size?',
                    answer: 'Yes, our products are designed to fit true to size. However, some styles may run slightly larger or smaller - this information is noted in the product description. Check customer reviews for additional fit feedback.'
                },
                {
                    question: 'How should I care for my items?',
                    answer: 'Care instructions are included on the label of each garment. Generally, we recommend cold water washing, gentle cycle, and hang drying for best results. Some items may require dry cleaning - please check individual care labels.'
                },
                {
                    question: 'Do you restock sold-out items?',
                    answer: 'Many of our popular items are restocked regularly. Sign up for restock notifications on the product page to be alerted when an item becomes available again. Limited edition items typically do not restock.'
                }
            ]
        },
        {
            category: 'Account & Privacy',
            questions: [
                {
                    question: 'Do I need an account to shop?',
                    answer: 'While you can browse without an account, you\'ll need to create one to complete a purchase. Having an account allows you to track orders, save favorite items, and enjoy a faster checkout experience.'
                },
                {
                    question: 'How do I reset my password?',
                    answer: 'Click "Forgot Password" on the login page and enter your email address. We\'ll send you a link to reset your password. If you don\'t receive the email within a few minutes, check your spam folder or contact customer service.'
                },
                {
                    question: 'Is my personal information secure?',
                    answer: 'Yes, we take your privacy seriously. All personal information is encrypted and securely stored. We never share or sell your data to third parties. Please review our Privacy Policy for more details.'
                },
                {
                    question: 'How do I unsubscribe from emails?',
                    answer: 'You can unsubscribe from marketing emails by clicking the "Unsubscribe" link at the bottom of any email. You\'ll still receive transactional emails about your orders. You can also manage email preferences in your account settings.'
                }
            ]
        }
    ];

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero Section */}
            <section className="bg-gray-50 py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                        <HelpCircle className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Find answers to common questions about our products, shipping, and policies
                    </p>
                </div>
            </section>

            {/* FAQs Section */}
            <section className="py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        {faqs.map((category, categoryIndex) => (
                            <div key={categoryIndex} className="mb-12 last:mb-0">
                                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                                    {category.category}
                                </h2>
                                <div className="space-y-4">
                                    {category.questions.map((faq, questionIndex) => {
                                        const globalIndex = `${categoryIndex}-${questionIndex}`;
                                        const isOpen = openIndex === globalIndex;

                                        return (
                                            <div
                                                key={questionIndex}
                                                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                                            >
                                                <button
                                                    onClick={() => toggleFAQ(globalIndex)}
                                                    className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors"
                                                >
                                                    <span className="text-lg font-medium text-gray-900 pr-4">
                                                        {faq.question}
                                                    </span>
                                                    {isOpen ? (
                                                        <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                                    )}
                                                </button>
                                                {isOpen && (
                                                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                                                        <p className="text-gray-600 leading-relaxed">
                                                            {faq.answer}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <div className="max-w-4xl mx-auto mt-16 p-6 lg:p-8 bg-gray-50 rounded-lg text-center">
                        <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 tracking-tight">
                            Still Have Questions?
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            Can't find the answer you're looking for? Our customer service team is here to help.
                        </p>
                        <div className="space-y-2">
                            <p className="text-gray-900 font-medium">
                                Email: support@studio.com
                            </p>
                            <p className="text-gray-900 font-medium">
                                Phone: 1-800-STUDIO-1 (Mon-Fri, 9am-6pm EST)
                            </p>
                        </div>
                        <button
                            onClick={() => window.location.href = '/template15/contact'}
                            className="mt-6 px-8 py-3 bg-gray-900 text-white font-medium rounded-md hover:bg-gray-800 transition-colors uppercase tracking-wide text-sm"
                        >
                            Contact Us
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default FAQs;
